import { Situation, UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { eventTracker } from '@hakimo-ui/hakimo/util';
import { Alert, Button, Textarea } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { AlertTriangle } from 'lucide-react';
import { usePreEscalate } from '../sop-workflow/escalate/usePreEscalate';
import {
  CHIP_CLASS,
  CHIP_FILL_CLASS,
  CHIP_IDLE_CLASS,
  CHIP_SELECTED_CLASS,
  SituationChips,
} from '../sop-workflow/utils';
import { MUTED_ICON_CLASS } from './sop-card-utils';
import { CollapsibleCard } from './CollapsibleCard';

const TITLE_CLASS =
  'text-onlight-text-1 dark:text-ondark-text-1 font-sans text-sm font-bold';

interface Props {
  locationId: string;
  locationAlarmId: string;
  selectedSituation?: Situation;
  product?: UnifiedMonitoringProduct;
  isAwaitingEscalation?: boolean;
  onEscalated: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  onBack: () => void;
}

export function StepEscalateDescription(props: Props) {
  const { selectedSituation, isAwaitingEscalation, onBack, onEscalated } =
    props;

  const {
    textValue,
    onTextValueChange,
    showEmptyError,
    onEscalate,
    subSituations,
    subSituation,
    setSubSituation,
    isLoading,
  } = usePreEscalate({
    locationId: props.locationId,
    locationAlarmId: props.locationAlarmId,
    navToEscalation: onEscalated,
    selectedSituation,
    product: props.product,
  });

  const isSubmitting = isLoading || isAwaitingEscalation;

  return (
    <div className="space-y-3">
      <CollapsibleCard
        variant="surface"
        title="Escalation Details"
        collapsible={false}
        ruled
        icon={<AlertTriangle className={MUTED_ICON_CLASS} />}
        bodyGap="space-y-3 pt-3"
      >
        {selectedSituation && (
          <div className="space-y-1">
            <div className={TITLE_CLASS}>Situation Selected</div>
            <SituationChips variant="filled" situations={[selectedSituation]} />
          </div>
        )}

        {subSituations.length > 0 && (
          <div className="space-y-1">
            <div className={TITLE_CLASS}>Sub Situation</div>
            <div className="flex flex-wrap items-start gap-2 py-1">
              {subSituations.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() =>
                    setSubSituation(item === subSituation ? undefined : item)
                  }
                  disabled={isLoading}
                  aria-pressed={item === subSituation}
                  className={clsx(
                    CHIP_CLASS,
                    'text-sm font-medium disabled:opacity-50',
                    item === subSituation
                      ? CHIP_SELECTED_CLASS
                      : clsx(CHIP_IDLE_CLASS, CHIP_FILL_CLASS)
                  )}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={TITLE_CLASS}>Description</div>
        {showEmptyError && (
          <Alert type="error" size="small">
            Please add a comment before proceeding.
          </Alert>
        )}
        <Textarea
          onChange={onTextValueChange}
          value={textValue}
          rows={3}
          placeholder="Provide a clear and concise description..."
        />
      </CollapsibleCard>

      {isAwaitingEscalation && (
        <Alert type="info" size="small">
          Waiting for the escalation to be created. Use Back to return to the
          overview if it does not appear.
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onEscalate}
          loading={isSubmitting}
          disabled={isSubmitting}
          className="flex-1"
          onSideEffect={eventTracker('create_location_incident')}
        >
          Proceed with Escalation
        </Button>
      </div>
    </div>
  );
}

export default StepEscalateDescription;
